import { Link } from 'react-router-dom';
import { FileText, MessageSquare, Archive, Users } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useState, useEffect, useMemo, memo } from 'react';
import { apiClient } from '../services/apiClient';

// Memoized Case Card Component
const CaseCard = memo(({ caseItem }: { caseItem: any }) => {
  const formattedDate = useMemo(() => 
    new Date(caseItem.createdAt).toLocaleDateString('el-GR'),
    [caseItem.createdAt]
  );

  const urgencyLabel = useMemo(() => {
    switch(caseItem.urgencyLevel) {
      case 'urgent': return 'ΕΠΕΙΓΟΝ';
      case 'high': return 'Υψηλή';
      case 'normal': return 'Κανονική';
      default: return 'Εκπαιδευτικό';
    }
  }, [caseItem.urgencyLevel]);

  const urgencyClass = useMemo(() => {
    switch(caseItem.urgencyLevel) {
      case 'urgent': return 'bg-red-200 text-red-800 font-medium';
      case 'high': return 'bg-orange-200 text-orange-800';
      case 'normal': return 'bg-green-200 text-green-800';
      default: return 'bg-gray-200 text-gray-800';
    }
  }, [caseItem.urgencyLevel]);

  return (
    <div className={`relative ${
      caseItem.urgencyLevel === 'urgent' 
        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 rounded-lg' 
        : 'bg-gray-50 dark:bg-gray-700 rounded-lg'
    }`}>
      <Link to={`/cases/${caseItem.id}`} className={`block p-4 h-full rounded-lg ${
        caseItem.urgencyLevel === 'urgent' 
          ? 'hover:bg-red-100 dark:hover:bg-red-900/30' 
          : 'hover:bg-gray-100 dark:hover:bg-gray-600'
      }`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center gap-2 mb-2">
            {caseItem.urgencyLevel === 'urgent' && (
              <span className="text-red-600 text-sm animate-pulse" title="Επείγον">
                🚨
              </span>
            )}
            <h4 className={`text-base font-medium line-clamp-2 ${
              caseItem.urgencyLevel === 'urgent' ? 'text-red-900 dark:text-red-300' : 'text-gray-900 dark:text-white'
            }`}>
              {caseItem.title}
            </h4>
          </div>
          {caseItem.description && (
            <p className={`text-sm mb-2 ${
              caseItem.urgencyLevel === 'urgent' ? 'text-red-700 dark:text-red-400' : 'text-gray-600 dark:text-gray-300'
            }`}>
              {caseItem.description.substring(0, 150)}{caseItem.description.length > 150 ? '...' : ''}
            </p>
          )}
          <div className="flex-1 flex flex-col justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              {caseItem.caseType}
            </p>
            <div className="flex justify-between items-end">
              <span className={`text-sm px-2 py-1 rounded-full ${urgencyClass}`}>
                {urgencyLabel}
              </span>
              <span className="text-sm text-gray-400 dark:text-gray-500">
                {formattedDate}
              </span>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
});

// Loading Skeleton Component
const CaseCardSkeleton = () => (
  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 animate-pulse">
    <div className="h-5 bg-gray-200 dark:bg-gray-600 rounded w-3/4 mb-2"></div>
    <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-full mb-2"></div>
    <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-2/3 mb-4"></div>
    <div className="flex justify-between items-end">
      <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded-full w-20"></div>
      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-16"></div>
    </div>
  </div>
);

export function HomePage() {
  const { isAuthenticated, user } = useAuth();
  const [priorityCases, setPriorityCases] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true); // Start with loading true for immediate skeletons
  const [hasLoadedData, setHasLoadedData] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !hasLoadedData) {
      fetchDashboardData();
      setHasLoadedData(true);
    } else if (!isAuthenticated) {
      setLoading(false); // Don't show loading for non-authenticated users
    }
  }, [isAuthenticated, hasLoadedData]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch all data in parallel for better performance
      const [urgentResponse, recentResponse, postsResponse, announcementsResponse] = await Promise.allSettled([
        // Fetch urgent cases
        apiClient.get('/cases?urgencyLevel=urgent&status=open&limit=10'),
        // Fetch recent cases (we'll filter later)
        apiClient.get('/cases?limit=8&sortBy=created_at&sortOrder=desc&status=open'),
        // Fetch recent forum posts (excluding announcements) sorted by latest activity, ignore pinning for homepage
        apiClient.get('/forum?path=posts&limit=5&excludeCategory=Ανακοινώσεις&sort_by=latest_activity&sort_order=desc&ignore_pinning=true'),
        // Fetch announcements
        apiClient.get('/forum?path=posts&categoryName=Ανακοινώσεις&limit=5&sortBy=created_at&sortOrder=desc')
      ]);

      // Process urgent cases
      const urgentCases = urgentResponse.status === 'fulfilled' 
        ? urgentResponse.value.data.data?.cases || []
        : [];

      // Process recent cases
      let recentCases = [];
      if (recentResponse.status === 'fulfilled') {
        recentCases = recentResponse.value.data.data?.cases || [];
        // Filter out urgent cases to avoid duplicates
        recentCases = recentCases.filter((c: any) => c.urgencyLevel !== 'urgent');
      }

      // Combine urgent cases first, then recent cases (up to 4 total)
      const combinedCases = [...urgentCases, ...recentCases].slice(0, 4);
      setPriorityCases(combinedCases);

      // Process forum posts
      if (postsResponse.status === 'fulfilled') {
        setRecentPosts(postsResponse.value.data.data?.posts || []);
      } else {
        setRecentPosts([]);
      }

      // Process announcements
      if (announcementsResponse.status === 'fulfilled') {
        setNews(announcementsResponse.value.data.data?.posts || []);
      } else {
        // If announcements category doesn't exist or error occurred
        setNews([]);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 transition-colors duration-300">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary-50 via-medical-50 to-clinical-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 transition-colors duration-300">
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isAuthenticated ? 'py-6' : 'py-24'}`}>
          <div className="text-center">
            <div className={`flex justify-center ${isAuthenticated ? 'mb-4' : 'mb-8'}`}>
              <div className={`${isAuthenticated ? 'w-16 h-16' : 'w-20 h-20'} bg-gradient-to-br from-medical-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg`}>
                <span className={`text-white font-bold ${isAuthenticated ? 'text-2xl' : 'text-3xl'}`}>🔬</span>
              </div>
            </div>
            <h1 className={`${isAuthenticated ? 'text-3xl sm:text-4xl' : 'text-5xl sm:text-6xl'} font-bold text-primary-900 dark:text-white tracking-tight transition-colors duration-300`}>
              Pathiety
            </h1>
            <p className={`${isAuthenticated ? 'mt-1 text-base' : 'mt-2 text-lg'} font-medium text-primary-600 dark:text-gray-300 transition-colors duration-300`}>
              Ελληνική Κοινότητα Παθολογοανατόμων
            </p>
            {!isAuthenticated && (
              <p className="mt-8 text-xl text-primary-700 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">
                Επαγγελματική πλατφόρμα για συνεργασία, εκπαίδευση και ανταλλαγή γνώσεων 
                μεταξύ Ελλήνων Παθολογοανατόμων. Μοιραστείτε περιστατικά, λάβετε ειδικές γνώμες 
                και συμμετάσχετε σε επαγγελματικές συζητήσεις.
              </p>
            )}
            
            {!isAuthenticated ? (
              <div className="mt-12 flex flex-col sm:flex-row justify-center gap-4 sm:gap-6 px-4">
                <Link
                  to="/register"
                  className="bg-gradient-to-r from-medical-500 to-medical-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:from-medical-600 hover:to-medical-700 transition-all duration-200 shadow-lg hover:shadow-xl text-center"
                >
                  Εγγραφή Μέλους
                </Link>
                <Link
                  to="/login"
                  className="bg-white text-medical-600 px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold border-2 border-medical-500 hover:bg-medical-50 transition-all duration-200 shadow-md hover:shadow-lg text-center"
                >
                  Σύνδεση
                </Link>
              </div>
            ) : (
              <div className="mt-6 px-4">
                <p className="text-base sm:text-lg text-primary-700 dark:text-gray-300 mb-4 font-medium text-center transition-colors duration-300">
                  Καλώς ήρθατε, Δρ. {user?.firstName} {user?.lastName}!
                </p>
                <div className="flex justify-center">
                  <Link
                    to="/cases/new"
                    className="bg-gradient-to-r from-medical-500 to-medical-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:from-medical-600 hover:to-medical-700 transition-all duration-200 inline-flex items-center shadow-lg hover:shadow-xl text-center"
                  >
                    <FileText className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3" />
                    <span className="text-sm sm:text-base">Υποβολή Νέου Περιστατικού</span>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Features Section - Only show for non-authenticated users */}
      {!isAuthenticated && (
        <div className="py-24 bg-white dark:bg-gray-900 transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-4xl font-bold text-primary-900 dark:text-white mb-4 transition-colors duration-300">
                Επαγγελματικές Λειτουργίες
              </h2>
              <p className="text-xl text-primary-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed transition-colors duration-300">
                Πλήρης σουίτα εργαλείων για την υποστήριξη της καθημερινής 
                παθολογοανατομικής πράξης και συνεχούς εκπαίδευσης
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10 px-4 sm:px-0">
              {/* Feature 1 */}
              <div className="text-center">
                <div className="mx-auto w-20 h-20 bg-gradient-to-br from-medical-100 to-medical-200 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <FileText className="w-10 h-10 text-medical-600" />
                </div>
                <h3 className="text-xl font-bold text-primary-900 dark:text-white mb-3 transition-colors duration-300">
                  Κλινικά Περιστατικά
                </h3>
                <p className="text-primary-600 dark:text-gray-300 leading-relaxed transition-colors duration-300">
                  Υποβολή και αξιολόγηση παθολογοανατομικών περιστατικών με 
                  δυνατότητα λήψης ειδικών γνωμών από έμπειρους συναδέλφους
                </p>
              </div>

              {/* Feature 2 */}
              <div className="text-center">
                <div className="mx-auto w-20 h-20 bg-gradient-to-br from-clinical-100 to-clinical-200 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <MessageSquare className="w-10 h-10 text-clinical-600" />
                </div>
                <h3 className="text-xl font-bold text-primary-900 dark:text-white mb-3 transition-colors duration-300">
                  Επιστημονικό Φόρουμ
                </h3>
                <p className="text-primary-600 dark:text-gray-300 leading-relaxed transition-colors duration-300">
                  Επαγγελματικές συζητήσεις, παρουσίαση ερευνητικών δεδομένων 
                  και ανταλλαγή καινοτόμων τεχνικών και μεθοδολογιών
                </p>
              </div>

              {/* Feature 3 */}
              <div className="text-center">
                <div className="mx-auto w-20 h-20 bg-gradient-to-br from-success-100 to-success-200 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <Archive className="w-10 h-10 text-success-600" />
                </div>
                <h3 className="text-xl font-bold text-primary-900 dark:text-white mb-3 transition-colors duration-300">
                  Εκπαιδευτικό Αρχείο
                </h3>
                <p className="text-primary-600 dark:text-gray-300 leading-relaxed transition-colors duration-300">
                  Οργανωμένη βιβλιοθήκη επιλυμένων περιστατικών με διαγνωστικά 
                  κριτήρια και εκπαιδευτικό υλικό για συνεχή μάθηση
                </p>
              </div>

              {/* Feature 4 */}
              <div className="text-center">
                <div className="mx-auto w-20 h-20 bg-gradient-to-br from-warning-100 to-warning-200 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <Users className="w-10 h-10 text-warning-600" />
                </div>
                <h3 className="text-xl font-bold text-primary-900 dark:text-white mb-3 transition-colors duration-300">
                  Επαγγελματικό Δίκτυο
                </h3>
                <p className="text-primary-600 dark:text-gray-300 leading-relaxed transition-colors duration-300">
                  Σύνδεση με συναδέλφους, ανάπτυξη συνεργασιών και δημιουργία 
                  επαγγελματικών σχέσεων στον χώρο της Παθολογικής Ανατομικής
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dashboard Sections - Only show for authenticated users */}
      {isAuthenticated && (
        <div className="py-16 bg-white dark:bg-gray-900 transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Cases Section - Full Width */}
            <div className="mb-8 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 transition-colors duration-300">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white transition-colors duration-300">
                    Πρόσφατα Περιστατικά
                  </h3>
                  <Link to="/cases" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium transition-colors duration-300">
                    Δείτε όλα →
                  </Link>
                </div>
              </div>
              <div className="p-6">
                {loading && priorityCases.length === 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, index) => (
                      <CaseCardSkeleton key={index} />
                    ))}
                  </div>
                ) : priorityCases.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {priorityCases.slice(0, 4).map((caseItem: any) => (
                      <CaseCard key={caseItem.id} caseItem={caseItem} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3 transition-colors duration-300" />
                    <p className="text-gray-500 dark:text-gray-400 transition-colors duration-300">Δεν υπάρχουν περιστατικά προτεραιότητας</p>
                  </div>
                )}
              </div>
            </div>

            {/* Forum Posts and News - Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

              {/* Recent Forum Posts Section */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 transition-colors duration-300">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white transition-colors duration-300">
                      Πρόσφατες Συζητήσεις
                    </h3>
                    <Link to="/forum" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium transition-colors duration-300">
                      Δείτε όλες →
                    </Link>
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="text-center">
                          <MessageSquare className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3 transition-colors duration-300" />
                          <p className="text-gray-500 dark:text-gray-400 transition-colors duration-300">Φορτώνονται συζητήσεις...</p>
                        </div>
                      </div>
                    ) : recentPosts.length > 0 ? (
                      recentPosts.slice(0, 5).map((post: any) => (
                        <div key={post.id} className="border-b border-gray-100 dark:border-gray-700 last:border-b-0 pb-3 last:pb-0 transition-colors duration-300">
                          <Link to={`/forum/post/${post.id}`} className="block hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded transition-colors duration-300">
                            <div className="flex flex-col">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-base font-medium text-gray-900 dark:text-white line-clamp-2 transition-colors duration-300">
                                    {post.title}
                                  </h4>
                                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 transition-colors duration-300">
                                    {post.author_name} • {post.category_name}
                                  </p>
                                </div>
                              </div>
                              {post.preview_content && (
                                <div className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2 transition-colors duration-300">
                                  <span dangerouslySetInnerHTML={{ 
                                    __html: post.preview_content.replace(/<[^>]*>/g, '').substring(0, 120) + (post.preview_content.length > 120 ? '...' : '') 
                                  }} />
                                </div>
                              )}
                              {post.last_reply_author && (
                                <div className="text-xs text-blue-600 dark:text-blue-400 mt-2 transition-colors duration-300">
                                  Τελευταία απάντηση: {post.last_reply_author}
                                </div>
                              )}
                            </div>
                          </Link>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <MessageSquare className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3 transition-colors duration-300" />
                        <p className="text-gray-500 dark:text-gray-400 transition-colors duration-300">Δεν υπάρχουν πρόσφατες συζητήσεις</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* News Section */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 transition-colors duration-300">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white transition-colors duration-300">
                      Ανακοινώσεις
                    </h3>
                    {(user?.role === 'admin' || user?.role === 'moderator') && (
                      <Link to="/admin/forum" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium transition-colors duration-300">
                        Διαχείριση →
                      </Link>
                    )}
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="text-center">
                          <Archive className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3 transition-colors duration-300" />
                          <p className="text-gray-500 dark:text-gray-400 transition-colors duration-300">Φορτώνονται ανακοινώσεις...</p>
                        </div>
                      </div>
                    ) : news.length > 0 ? (
                      news.slice(0, 5).map((announcement: any) => (
                        <div key={announcement.id} className="border-b border-gray-100 dark:border-gray-700 last:border-b-0 pb-3 last:pb-0 transition-colors duration-300">
                          <Link to={`/forum/post/${announcement.id}`} className="block hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded transition-colors duration-300">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <h4 className="text-base font-medium text-gray-900 dark:text-white truncate transition-colors duration-300">
                                  {announcement.title}
                                </h4>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 transition-colors duration-300">
                                  {announcement.author_name}
                                </p>
                              </div>
                              <span className="text-sm text-gray-400 dark:text-gray-500 transition-colors duration-300">
                                {new Date(announcement.created_at).toLocaleDateString('el-GR')}
                              </span>
                            </div>
                          </Link>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <Archive className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3 transition-colors duration-300" />
                        <p className="text-gray-500 dark:text-gray-400 transition-colors duration-300">Δεν υπάρχουν ανακοινώσεις</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section - Only show for non-authenticated users */}
      {!isAuthenticated && (
        <div className="bg-gradient-to-r from-medical-600 to-primary-700 dark:from-gray-800 dark:to-gray-900 transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="text-center">
              <h2 className="text-4xl font-bold text-white dark:text-gray-100 mb-6 transition-colors duration-300">
                Ενταχθείτε στην Ελληνική Κοινότητα Παθολογοανατόμων
              </h2>
              <p className="text-xl text-medical-100 dark:text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">
                Συμμετάσχετε σε μια επαγγελματική κοινότητα που υποστηρίζει τη συνεχή 
                εκπαίδευση, την έρευνα και την ανταλλαγή εμπειριών στην Παθολογική Ανατομική
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6 px-4">
                <Link
                  to="/register"
                  className="bg-white text-medical-600 px-8 sm:px-10 py-3 sm:py-4 rounded-xl font-bold hover:bg-medical-50 transition-all duration-200 shadow-xl hover:shadow-2xl text-center"
                >
                  Εγγραφή Μέλους
                </Link>
                <Link
                  to="/cases"
                  className="bg-medical-700 bg-opacity-20 backdrop-blur text-white px-8 sm:px-10 py-3 sm:py-4 rounded-xl font-semibold border-2 border-medical-300 hover:bg-opacity-30 transition-all duration-200 text-center"
                >
                  Περιήγηση Περιστατικών
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}